import React from 'react';
import { Checkbox } from '@mui/material';
import Button from 'components/atoms/Button';
import { getHtmlText } from 'components/organisms/HtmlSlate';
import { EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import { findInDictionary, parseDictionary } from 'helpers/dictionary';
import { IDictionary } from 'models/dictionary';
import { ITableColumns, ITableDataSource } from 'models/table';
import { ILegalEntityExtendedAddress } from 'models/legalEntity';
import {
  ILicenceTemplateApplicationProcessResponse,
  ILicenceSafeStorage,
} from 'models/licenceConfigurator';
import { formatGPSField } from 'utils/form';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import Icon from 'components/atoms/Icon/Icon';
import { priceFormat } from 'helpers/price';
import { ILicenceAttachment } from 'models/licence';
import { appendExtensionToFilename } from '../../../utils/files';

interface ILicenceApplicationProcessTableColumns {
  selectedPrice: string;
  setSelectedPrice: (arg: string) => void;
  handleView: (arg: ITableDataSource) => void;
}

export const licenceApplicationProcessTableColumns = ({
  selectedPrice,
  setSelectedPrice,
  handleView,
}: ILicenceApplicationProcessTableColumns): ITableColumns[] => [
    {
      id: 'f0504ca5-6b32-482d-b369-304a6f32f512',
      key: 'select',
      title: '',
      isSortable: false,
      width: '42px',
      render: (_data, props) => (
        <Checkbox
          id={`legalEntity_licence_selectPrice-checkbox-${props.id}`}
          checkedIcon={<Icon type={EIconTypes.aroundChecked} fontSize={20} />}
          icon={<Icon type={EIconTypes.aroundCheckbox} fontSize={20} />}
          name={`selected-${props.id}`}
          checked={selectedPrice === props.id
          }
          onChange={() => {
            setSelectedPrice(props.id);
          }}
          sx={{ padding: 0 }}
        />
      ),
    },
    {
      id: '36fa4777-5f90-434a-adf1-2b3aea11d685',
      key: 'type',
      title: 'licence.licencePricesTable.type.column',
      isSortable: false,
      width: '180px',
      align: 'center',
      render: (_data, props) => (
        <Chips variant={EChipsVariants.warning} text={props.type} fullWidth />
      ),
    },
    {
      id: 'c3c4e92a-8276-4695-9b6d-9c3566e9501a',
      key: 'fee',
      width: '112px',
      align: 'center',
      isCenterTableHead: true,
      title: 'licence.licencePricesTable.fee.column',
      isSortable: false,
    },
    {
      id: '063918e4-7492-40b2-9445-4d8d43e24f07',
      key: 'name',
      title: 'licence.licencePricesTable.name.column',
      isSortable: false,
    },
    {
      id: 'a463ea1c-4c9f-4c64-aac4-01fb85c2bb66',
      key: 'description',
      title: 'licence.licencePricesTable.description.column',
      isSortable: false,
    },
    {
      id: '9c4ca471-15cd-4e22-90ef-7b57b7622955',
      key: '',
      title: 'licence.licencePricesTable.action.column',
      isSortable: false,
      width: '80px',
      align: 'center',
      isCenterTableHead: true,
      render: (_data, props) => (
        <Button
          variant={EButtonVariants.outlined}
          tableButton
          icon={EIconTypes.view}
          onClick={() => handleView(props)}
        />
      ),
    },
  ];

export const mapLicenceApplicationProcessToDataSource = (
  items: ILicenceTemplateApplicationProcessResponse[],
  applicationProcessTypes: IDictionary[] | null,
  currency: string,
): ITableDataSource[] =>
  items.map((item) => ({
    id: item.uniqueId,
    type: findInDictionary(item.processTypeUniqueId, applicationProcessTypes),
    fee: `${priceFormat(item.priceFee)} ${currency}`,
    name: item.priceName,
    description: getHtmlText(item.priceDescription, 80),
  }));

interface ILegalEntityAddressTableColumns {
  selectedStorage: ILicenceSafeStorage[];
  setSelectedStorage: (storage: ILicenceSafeStorage[]) => void;
  t: (s: string) => string;
}

export const mapLEExtendedAddressToSafeStorage = (
  items: ILegalEntityExtendedAddress[],
  region: IDictionary[] | null,
  country: IDictionary[] | null,
  type: IDictionary[] | null,
  legalEntityId: string,
) =>
  items
    .map((item) => ({
      id: item.uniqueId,
      addressUniqueId: item.uniqueId,
      typeUniqueId: item.typeUniqueId,
      typeName: findInDictionary(item.typeUniqueId, type),
      line1: item.line1,
      line2: item.line2,
      city: item.city,
      postalCode: item.postalCode,
      regionUniqueId: item.regionUniqueId,
      regionName: findInDictionary(item.regionUniqueId, region),
      countryUniqueId: item.countryUniqueId,
      countryName: findInDictionary(item.countryUniqueId, country),
      gps: formatGPSField(item.latitude, item.longitude),
      latitude: item.latitude,
      longitude: item.longitude,
      isDefault: item.isDefault,
      storage: item.storage,
      firearmAmount: item.firearmAmount,
      ancillaryAmount: item.ancillaryAmount,
      legalEntityId,
    }))
    .filter((address) => address?.storage);

interface IStorageTableData {
  id: string | number;
  addressUniqueId?: string;
  uniqueId?: string;
  typeUniqueId?: string;
  line1?: string;
  line2?: string;
  city?: string;
  postalCode?: string;
  regionUniqueId?: string;
  countryUniqueId?: string;
  latitude?: number;
  longitude?: number;
  storage?: boolean;
  isDefault?: boolean;
}

const mapDataToStorage = (storageData: IStorageTableData): ILicenceSafeStorage => {
  const storage: ILicenceSafeStorage = {
    addressUniqueId: storageData.addressUniqueId || storageData.uniqueId || '',
    typeUniqueId: storageData.typeUniqueId || '',
    line1: storageData.line1 || '',
    line2: storageData.line2 || '',
    city: storageData.city || '',
    postalCode: storageData.postalCode || '',
    regionUniqueId: storageData.regionUniqueId || '',
    countryUniqueId: storageData.countryUniqueId || '',
    latitude: storageData.latitude || 0,
    longitude: storageData.longitude || 0,
    storage: storageData.storage !== undefined ? storageData.storage : true,
    isDefault: storageData.isDefault || false,
  };

  return storage;
};

export const safeStorageTableColumns = ({
  selectedStorage,
  setSelectedStorage,
  t,
}: ILegalEntityAddressTableColumns): ITableColumns[] => [
    {
      id: '3d5f7a9c-6e2b-4c8d-1f7a-9c3d5f7a2e4c',
      key: 'select',
      title: t('legalEntityDetails.storage.select.column'),
      isSortable: false,
      render: (_data, props, index) => {
        const targetItem = mapDataToStorage(props);
        const isChecked = selectedStorage.some(
          (item) => item.addressUniqueId === targetItem.addressUniqueId,
        );

        return (
          <Checkbox
            id={`legalEntity_licence_selectSafeStorage-checkbox-${props.id}`}
            name={`safeStorage${index}`}
            checked={isChecked}
            onChange={(e) => {
              if (e.target.checked) {
                const newSelectedStorage = selectedStorage.filter(
                  (item) => item.addressUniqueId !== targetItem.addressUniqueId,
                );
                setSelectedStorage([...newSelectedStorage, targetItem]);
              } else {
                setSelectedStorage(
                  selectedStorage.filter(
                    (item) => item.addressUniqueId !== targetItem.addressUniqueId,
                  ),
                );
              }
            }}
          />
        );
      },
    },
    {
      id: '8a2b9c7d-4e6f-4a1b-9c3d-5e7f8a2b9c7d',
      key: 'line',
      title: 'legalEntityDetails.addressTab.address.column',
      isSortable: false,
      render: (_data, props) => `${props.line1} ${props.line2}`,
    },
    {
      id: '1f3e5d7c-8b4a-4d2e-8f1a-6c9b2e4d7f1a',
      key: 'city',
      title: 'legalEntityDetails.addressTab.city.column',
      isSortable: false,
    },
    {
      id: '4c6e8a2d-1f5b-4e3f-7a9c-2d4f6e8a1c3e',
      key: 'regionName',
      title: 'legalEntityDetails.addressTab.stateProvince.column',
      isSortable: false,
    },
    {
      id: '7f1a3c5e-2d8b-4a6f-1e9c-5f7a3c2e4d6f',
      key: 'countryName',
      title: 'legalEntityDetails.addressTab.country.column',
      isSortable: false,
    },
    {
      id: '2e4d6f8a-5c1e-4b7d-3f5a-8e2d4f6a1c5e',
      key: 'gps',
      title: 'legalEntityDetails.addressTab.gps.column',
      isSortable: false,
    },
    {
      id: '5a7c9e1f-8d4b-4f6a-2c8e-1f5a7c9e2d4f',
      key: 'postalCode',
      title: 'legalEntityDetails.addressTab.zipPostalCode.column',
      isSortable: false,
    },
  ];

export const mapAttachmentsToDataSource = (
  items: ILicenceAttachment[] | undefined,
  documentsType: IDictionary[] | null,
): ITableDataSource[] => {
  if (!items) return [];
  return items.map((item) => ({
    id: item.uniqueId,
    name: appendExtensionToFilename(item.name, item.fileType),
    fileType: item.fileType,
    weaponLicenceDocumentType:
      item.type && documentsType ? parseDictionary(documentsType, 'name')[item.type] : '',
    comment: item.comment,
    file: null,
  }));
};

interface IDocumentsTableColumns {
  handleDownload: (file: File) => void;
  handleDownloadAttachment: (id: string, name: string) => void;
  handleDelete: (id: number) => void;
  handleDeleteAttachment: (id: string) => void;
}
export const documentsTableColumns = ({
  handleDownload,
  handleDownloadAttachment,
  handleDelete,
  handleDeleteAttachment,
}: IDocumentsTableColumns): ITableColumns[] => [
    {
      id: '91fc63ee-934e-4158-abe6-528e1a0c02ba',
      key: 'name',
      title: 'legalEntity.licence.documentName.label',
      isSortable: false,
    },
    {
      id: '4ec43361-416f-4dfd-af32-57db12f7ee76',
      key: 'weaponLicenceDocumentType',
      title: 'legalEntity.licence.documentType.label',
      isSortable: false,
    },
    {
      id: '90fe8519-0624-4c1b-9ece-cd907112e307',
      key: 'comment',
      title: 'legalEntity.licence.comment.label',
      isSortable: false,
    },
    {
      id: '92d362bd-83f2-4c30-889f-308917ce68c2',
      key: '',
      title: 'legalEntity.licence.action.column',
      isSortable: false,
      width: '100px',
      align: 'center',
      isCenterTableHead: true,
      render: (_data, props) => (
        <>
          <Button
            id={`licence_vary_attachment_download-button-${props.id}`}
            variant={EButtonVariants.outlined}
            tableButton
            icon={EIconTypes.download}
            onClick={() =>
              props.file
                ? handleDownload(props.file)
                : handleDownloadAttachment(props.id, props.name)
            }
          />
          <Button
            id={`licence_vary_attachment_delete-button-${props.id}`}
            variant={EButtonVariants.outlined}
            tableButton
            icon={EIconTypes.delete}
            onClick={() =>
              props.file ? handleDelete(props.id) : handleDeleteAttachment(props.id)
            }
          />
        </>
      ),
    },
  ];
